import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

// THE LIVING COVER'S WORDS ON SCREEN (owner 2026-10-22): the release versions ("Out now", "Out Friday", the
// countdowns, "Pre-save") and the lyrics, shown on the page before paying the way the videos draw them
// (services/quicktools/release.js and lyrics.js: Anton in the site's gold, a soft shadow).

export const RELEASE_KINDS = ['outnow', 'outon', '7days', '3days', 'tomorrow', 'presave'] as const;
export type ReleaseKind = (typeof RELEASE_KINDS)[number];
export const RELEASE_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type TextLang = 'en' | 'es';

// The same words as release.js (the video's language is the buyer's pick, whatever the page is shown in).
const WORDS = {
  en: {
    kicker: 'NEW MUSIC', outnow: 'OUT NOW', outon: (d: string) => `OUT ${d}`, days: (n: number) => `IN ${n} DAYS`, tomorrow: 'OUT TOMORROW', presave: 'PRE-SAVE NOW',
    day: { mon: 'MONDAY', tue: 'TUESDAY', wed: 'WEDNESDAY', thu: 'THURSDAY', fri: 'FRIDAY', sat: 'SATURDAY', sun: 'SUNDAY' } as Record<string, string>,
    month: ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'],
    date: (m: string, d: number) => `${m} ${d}`,
  },
  es: {
    kicker: 'MÚSICA NUEVA', outnow: 'YA DISPONIBLE', outon: (d: string) => `SALE EL ${d}`, days: (n: number) => `EN ${n} DÍAS`, tomorrow: 'SALE MAÑANA', presave: 'HAZ PRE-SAVE',
    day: { mon: 'LUNES', tue: 'MARTES', wed: 'MIÉRCOLES', thu: 'JUEVES', fri: 'VIERNES', sat: 'SÁBADO', sun: 'DOMINGO' } as Record<string, string>,
    month: ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'],
    date: (m: string, d: number) => `${d} ${m}`,
  },
};

/** An ASS font size over a CSS one for Anton (its ascender plus descender, in ems). */
const ASS_EM = 1.46;

export interface ReleaseWords { kicker: string; big: string; sub: string; tag: string }

export function releaseWords(kind: ReleaseKind, { lang, day, songname, presave }: { lang: TextLang; day: string; songname: string; presave: string }): ReleaseWords {
  const w = WORDS[lang];
  const m = day.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const dayText = m ? w.date(w.month[Number(m[2]) - 1] || '', Number(m[3])) : w.day[day.slice(0, 3)] || w.day.fri;
  const big = kind === 'outnow' ? w.outnow : kind === 'outon' ? w.outon(dayText) : kind === '7days' ? w.days(7) : kind === '3days' ? w.days(3) : kind === 'tomorrow' ? w.tomorrow : w.presave;
  return { kicker: w.kicker, big, sub: songname.trim().slice(0, 60).toUpperCase(), tag: kind === 'presave' ? presave.trim().slice(0, 48) : '' };
}

/** The release text over a 9:16 picture, sized by the box it sits in (container query units). */
export function ReleaseCard({ words, className }: { words: ReleaseWords; className?: string }) {
  // The video fits the big line to 86% of the width (release.js), capped at 12% of the height. An ASS font size is
  // the font's whole line (Anton's is ~1.46 em), so the page's sizes are the video's divided by ASS_EM.
  const big = `calc(min(${(86 / (0.3 * Math.max(4, words.big.length))).toFixed(2)}cqw, 12cqh) / ${ASS_EM})`;
  return (
    // The box itself is the size container; its child is laid out in its units (a container's own padding cannot use them).
    <div className={cn('pointer-events-none absolute inset-0 overflow-hidden [container-type:size]', className)} aria-hidden>
      <div className="flex h-full flex-col items-center justify-center text-center" style={{ paddingTop: '20cqh' }}>
      {/* The video's soft dark band behind the words (release.js). */}
      <div className="flex w-full flex-col items-center bg-black/40 py-[3cqh] shadow-[0_0_6cqh_4cqh_rgba(0,0,0,0.4)]">
      <span className="font-['Anton',Impact,sans-serif] tracking-[0.45em] text-white [text-shadow:0_2px_6px_rgba(0,0,0,0.7)]" style={{ fontSize: '1.8cqh' }}>{words.kicker}</span>
      <span className="whitespace-nowrap font-['Anton',Impact,sans-serif] leading-[1.15] text-[#F4BE52] [text-shadow:0_3px_10px_rgba(0,0,0,0.75)]" style={{ fontSize: big }}>{words.big}</span>
      {words.sub && <span className="mt-[0.6cqh] font-bold tracking-[0.06em] text-white [text-shadow:0_2px_6px_rgba(0,0,0,0.7)]" style={{ fontSize: '2.4cqh' }}>{words.sub}</span>}
      {words.tag && <span className="mt-[1.4cqh] bg-[#F4BE52] px-[1.4cqw] py-[0.4cqh] font-bold text-[#10131a]" style={{ fontSize: '2.1cqh' }}>{words.tag}</span>}
      </div>
      </div>
    </div>
  );
}

/**
 * THE FINISH STEP'S RELEASE VERSIONS: tap the ones you want (each a short video with the words on it), the day for
 * "Out Friday", the link text for "Pre-save", and the words' language. Each picked one is shown as it will look;
 * a tap shows it big on the picture (onShow).
 */
export function ReleasePicker({ value, onChange, day, onDay, presave, onPresave, lang, onLang, songname, picture, shown, onShow }: {
  value: string;
  onChange: (v: string) => void;
  day: string;
  onDay: (v: string) => void;
  presave: string;
  onPresave: (v: string) => void;
  lang: TextLang;
  onLang: (v: TextLang) => void;
  songname: string;
  picture: string | null;
  shown: ReleaseKind | null;
  onShow: (k: ReleaseKind | null) => void;
}) {
  const { t } = useTranslation();
  const r = (key: string, opts?: Record<string, unknown>) => t(`qt.tools.canvas.fields.release.${key}`, opts);
  const picked = value.split(',').filter((v): v is ReleaseKind => (RELEASE_KINDS as readonly string[]).includes(v));
  const toggle = (k: ReleaseKind) => {
    const on = picked.includes(k);
    const next = RELEASE_KINDS.filter((x) => (x === k ? !on : picked.includes(x)));
    onChange(next.join(','));
    onShow(on ? null : k);
  };
  const isDate = /^\d{4}-\d{2}-\d{2}$/.test(day);
  const input = 'w-full rounded-xl border border-white/15 bg-[hsl(220_18%_7%)] px-3.5 py-2.5 text-[15px] text-foreground outline-none focus:border-primary/70';
  return (
    <div className="mt-5 rounded-2xl bg-white/[0.04] p-3" data-testid="qt-release">
      <p className="text-[14px] font-semibold text-foreground">{r('label')}</p>
      <p className="mt-0.5 text-[12.5px] leading-snug text-muted-foreground">{r('hint')}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {RELEASE_KINDS.map((k) => {
          const on = picked.includes(k);
          return (
            <button key={k} type="button" onClick={() => toggle(k)} aria-pressed={on} data-testid={`qt-release-${k}`}
              className={cn('inline-flex min-h-[40px] items-center gap-1.5 rounded-full border px-3.5 text-[13.5px] font-semibold transition-colors',
                on ? 'border-primary bg-primary/15 text-foreground' : 'border-white/15 bg-white/[0.03] text-muted-foreground hover:text-foreground')}>
              {on && <Check className="h-4 w-4 text-primary" aria-hidden />}
              {r(`options.${k}`)}
            </button>
          );
        })}
      </div>
      {picked.includes('outon') && (
        <div className="mt-3" data-testid="qt-release-day">
          <p className="mb-1.5 text-[13px] font-medium text-muted-foreground">{r('day')}</p>
          <div className="flex flex-wrap gap-1.5">
            {RELEASE_DAYS.map((d) => (
              <button key={d} type="button" onClick={() => onDay(d)} aria-pressed={!isDate && (day || 'fri') === d}
                className={cn('min-h-[36px] rounded-full px-3 text-[13px] font-semibold', !isDate && (day || 'fri') === d ? 'bg-primary text-primary-foreground' : 'bg-white/[0.06] text-muted-foreground')}>
                {r(`days.${d}`)}
              </button>
            ))}
          </div>
          <label className="mt-2 flex items-center gap-2 text-[13px] text-muted-foreground">
            {r('orDate')}
            <input type="date" value={isDate ? day : ''} onChange={(e) => onDay(e.target.value || 'fri')} className={cn(input, 'w-auto py-1.5')} data-testid="qt-release-date" />
          </label>
        </div>
      )}
      {picked.includes('presave') && (
        <label className="mt-3 block">
          <span className="mb-1.5 block text-[13px] font-medium text-muted-foreground">{r('presave')}</span>
          <input value={presave} maxLength={48} onChange={(e) => onPresave(e.target.value)} placeholder={r('presavePlaceholder')} className={input} data-testid="qt-release-presave" />
        </label>
      )}
      {picked.length > 0 && (
        <>
          <div className="mt-3 flex items-center gap-2 text-[13px] text-muted-foreground">
            {r('lang')}
            {(['en', 'es'] as const).map((l) => (
              <button key={l} type="button" onClick={() => onLang(l)} aria-pressed={lang === l} translate="no"
                className={cn('min-h-[32px] rounded-full px-3 text-[12.5px] font-bold', lang === l ? 'bg-primary text-primary-foreground' : 'bg-white/[0.06] text-muted-foreground')}>
                {l === 'en' ? 'English' : 'Español'}
              </button>
            ))}
          </div>
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="qt-release-previews">
            {picked.map((k) => (
              <button key={k} type="button" onClick={() => onShow(shown === k ? null : k)} aria-pressed={shown === k} aria-label={r('see', { name: r(`options.${k}`) })}
                className={cn('relative aspect-[9/16] w-[86px] shrink-0 overflow-hidden rounded-xl bg-black ring-2', shown === k ? 'ring-primary' : 'ring-transparent')}>
                {picture && <img src={picture} alt="" className="absolute inset-0 h-full w-full object-cover opacity-80" />}
                <ReleaseCard words={releaseWords(k, { lang, day: day || 'fri', songname, presave })} />
              </button>
            ))}
          </div>
          <p className="mt-1 text-[12px] leading-snug text-muted-foreground">{r('each')}</p>
        </>
      )}
    </div>
  );
}

/**
 * The lyrics in the live preview, roughly timed: the pasted lines spread over the song's singing (past a short
 * intro), the ones inside the part picked shown one at a time, each word turning gold as its turn comes. The made
 * video times them to the vocals instead (lyrics.js).
 */
export function previewLyricLines(text: string, songSeconds: number, start: number, len: number) {
  const lines = text.split(/\r?\n/).map((l) => l.replace(/\[[^\]]*\]/g, '').replace(/\s+/g, ' ').trim()).filter((l) => /[\p{L}\p{N}]/u.test(l)).slice(0, 240);
  if (!lines.length) return [];
  const intro = Math.min(8, songSeconds * 0.06);
  const span = songSeconds > len + 1 ? Math.max(len, songSeconds - intro - 2) : len;
  const from = songSeconds > len + 1 ? intro : 0;
  const weights = lines.map((l) => l.length + 8);
  const sum = weights.reduce((s, w) => s + w, 0);
  let x = from;
  const timed = lines.map((l, i) => {
    const d = (span * weights[i]) / sum;
    const out = { text: l, start: x - start, end: x + d * 0.92 - start };
    x += d;
    return out;
  });
  const inside = timed.filter((l) => (l.start + l.end) / 2 >= 0 && (l.start + l.end) / 2 <= len);
  return inside.length ? inside : timed.slice(0, 6).map((l, i) => ({ ...l, start: (i * len) / 6, end: ((i + 0.92) * len) / 6 }));
}

// The video's line is 5.8% of the height in ASS units (lyrics.js): 5.8 / ASS_EM = 4% of the height, 7% of the
// width, and this box is 84% wide.
/**
 * The words over the live preview, laid out as lyrics.js lays them on the file: low and centred, or `top` when the
 * subject sits low (canvas.js lyricsOnTop), sized by the frame's height (`ratio`: height / width, 16/9 for 9:16).
 */
export function LyricsOverlay({ lines, time, len, top = false, ratio = 16 / 9 }: { lines: { text: string; start: number; end: number }[]; time: number | null; len: number; top?: boolean; ratio?: number }) {
  // Without the song playing, a clock of its own runs through the part.
  const [clock, setClock] = useState(0);
  useEffect(() => {
    if (time !== null) return undefined;
    const began = performance.now();
    const id = window.setInterval(() => setClock(((performance.now() - began) / 1000) % len), 100);
    return () => window.clearInterval(id);
  }, [time, len]);
  const now = time ?? clock;
  const line = useMemo(() => lines.find((l) => now >= l.start - 0.2 && now < l.end + 0.3) || null, [lines, now]);
  if (!line) return null;
  const words = line.text.split(' ');
  const sung = Math.floor(((now - line.start) / Math.max(0.3, line.end - line.start)) * words.length);
  return (
    <div className={cn('pointer-events-none absolute inset-x-[8%] text-center [container-type:inline-size]', top ? 'top-[12%]' : 'bottom-[17%]')} aria-hidden data-testid="qt-lyrics-preview">
      {/* Its own box is the container, so the line sizes by the picture's width. */}
      <span className="font-['Anton',Impact,sans-serif] leading-[1.15] tracking-[0.01em] [text-shadow:0_2px_3px_rgba(0,0,0,0.75),0_4px_14px_rgba(0,0,0,0.6)]" style={{ fontSize: `${(8.4 * ratio) / (16 / 9)}cqw` }}>
        {words.map((w, i) => (
          <span key={i} className={i < sung ? 'text-[#F4BE52]' : 'text-white'}>{w}{i < words.length - 1 ? ' ' : ''}</span>
        ))}
      </span>
    </div>
  );
}
